import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { apiRequest } from "../../api/http";
import {
  OwnerCertificationDetail,
  OwnerCertificationListItem,
  PaginatedResponse,
  ReviewStatus,
} from "../../types/admin";

export type OwnerCertificationListQuery = {
  review_status?: ReviewStatus;
  q?: string;
  page?: number;
  page_size?: number;
};

type UpdateOwnerCertificationPayload = {
  branchId: number;
  review_status: ReviewStatus;
  review_note?: string;
};

type OwnerCertificationsState = {
  list: OwnerCertificationListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  loadingList: boolean;
  listError: string | null;
  detail: OwnerCertificationDetail | null;
  loadingDetail: boolean;
  detailError: string | null;
  updating: boolean;
  updateError: string | null;
};

const initialState: OwnerCertificationsState = {
  list: [],
  totalCount: 0,
  page: 1,
  pageSize: 20,
  loadingList: false,
  listError: null,
  detail: null,
  loadingDetail: false,
  detailError: null,
  updating: false,
  updateError: null,
};

const toQuery = (query: OwnerCertificationListQuery): string => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

export const fetchOwnerCertifications = createAsyncThunk<
  PaginatedResponse<OwnerCertificationListItem>,
  OwnerCertificationListQuery,
  { rejectValue: string }
>("ownerCertifications/fetchOwnerCertifications", async (query, { rejectWithValue }) => {
  try {
    return await apiRequest<PaginatedResponse<OwnerCertificationListItem>>(
      `/admin/owner-certifications${toQuery(query)}`
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : "사업주 인증 목록 조회에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const fetchOwnerCertificationDetail = createAsyncThunk<
  OwnerCertificationDetail,
  number,
  { rejectValue: string }
>("ownerCertifications/fetchOwnerCertificationDetail", async (branchId, { rejectWithValue }) => {
  try {
    return await apiRequest<OwnerCertificationDetail>(`/admin/owner-certifications/${branchId}`);
  } catch (error) {
    const message = error instanceof Error ? error.message : "사업주 인증 상세 조회에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const updateOwnerCertificationStatus = createAsyncThunk<
  OwnerCertificationDetail,
  UpdateOwnerCertificationPayload,
  { rejectValue: string }
>("ownerCertifications/updateOwnerCertificationStatus", async (payload, { rejectWithValue }) => {
  try {
    const { branchId, ...body } = payload;
    return await apiRequest<OwnerCertificationDetail>(`/admin/owner-certifications/${branchId}`, {
      method: "PATCH",
      body,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "사업주 인증 상태 변경에 실패했습니다.";
    return rejectWithValue(message);
  }
});

const ownerCertificationsSlice = createSlice({
  name: "ownerCertifications",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOwnerCertifications.pending, (state) => {
        state.loadingList = true;
        state.listError = null;
      })
      .addCase(fetchOwnerCertifications.fulfilled, (state, action) => {
        state.loadingList = false;
        state.list = action.payload.items;
        state.totalCount = action.payload.total_count;
        state.page = action.payload.page;
        state.pageSize = action.payload.page_size;
      })
      .addCase(fetchOwnerCertifications.rejected, (state, action) => {
        state.loadingList = false;
        state.listError = action.payload ?? "사업주 인증 목록 조회에 실패했습니다.";
      })
      .addCase(fetchOwnerCertificationDetail.pending, (state) => {
        state.loadingDetail = true;
        state.detailError = null;
      })
      .addCase(fetchOwnerCertificationDetail.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.detail = action.payload;
      })
      .addCase(fetchOwnerCertificationDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.detailError = action.payload ?? "사업주 인증 상세 조회에 실패했습니다.";
      })
      .addCase(updateOwnerCertificationStatus.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(updateOwnerCertificationStatus.fulfilled, (state, action) => {
        state.updating = false;
        state.detail = action.payload;
      })
      .addCase(updateOwnerCertificationStatus.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload ?? "사업주 인증 상태 변경에 실패했습니다.";
      });
  },
});

export default ownerCertificationsSlice.reducer;
